/**
 * Standardized error handling utility
 * Ensures consistent error response format across the application
 */

export class AppError extends Error {
  constructor(message, statusCode = 500, isOperational = true) {
    super(message);
    this.statusCode = statusCode;
    this.isOperational = isOperational;
    this.name = this.constructor.name;
    
    Error.captureStackTrace(this, this.constructor);
  }
}

/**
 * Creates a standardized success response
 */
export const createSuccessResponse = (data = null, message = 'Success') => {
  return {
    success: true,
    message,
    data,
    timestamp: new Date().toISOString()
  };
};

/**
 * Creates a standardized error response
 */
export const createErrorResponse = (error, statusCode = 500) => {
  const message = error instanceof Error ? error.message : String(error);
  
  return {
    success: false,
    message,
    error: message,
    statusCode,
    timestamp: new Date().toISOString()
  };
};

/**
 * Handles async operations with consistent error handling
 */
export const handleAsync = (fn) => {
  return async (...args) => {
    try {
      const result = await fn(...args);
      return result.success !== undefined ? result : createSuccessResponse(result);
    } catch (error) {
      console.error('Async operation failed:', error);
      return createErrorResponse(error);
    }
  };
};

/**
 * Validates required fields and throws AppError if missing
 */
export const validateRequiredFields = (data, requiredFields) => {
  const missingFields = requiredFields.filter(field => !data[field]);
  
  if (missingFields.length > 0) {
    throw new AppError(
      `Missing required fields: ${missingFields.join(', ')}`,
      400
    );
  }
};

/**
 * Handles GraphQL errors consistently
 */
export const handleGraphQLError = (error) => {
  if (error.errors && error.errors.length > 0) {
    const message = error.errors.map(e => e.message).join(', ');
    return createErrorResponse(new AppError(message, 400));
  }
  
  return createErrorResponse(error);
};